import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import Modal from "../../components/Modal";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiKey,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

const LifeSaverManagement = () => {
  const [lifeSavers, setLifeSavers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editingLifeSaver, setEditingLifeSaver] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    mobile: "",
    email: "",
    whatsapp_number: "",
    communication_address: "",
    wallet_address: "",
    username: "",
    password: "",
    role: "",
  });
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    fetchLifeSavers();
  }, []);

  const fetchLifeSavers = async () => {
    try {
      const response = await api.get("/life-savers");
      setLifeSavers(response.data);
    } catch (error) {
      toast.error("Failed to fetch Members");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (lifeSaver = null) => {
    if (lifeSaver) {
      setEditingLifeSaver(lifeSaver);
      setFormData({
        full_name: lifeSaver.full_name,
        mobile: lifeSaver.mobile,
        email: lifeSaver.email,
        whatsapp_number: lifeSaver.whatsapp_number || "",
        communication_address: lifeSaver.communication_address || "",
        wallet_address: lifeSaver.wallet_address || "",
        username: lifeSaver.login_username || "",
        password: "",
        role: lifeSaver.role || "",
      });
    } else {
      setEditingLifeSaver(null);
      setFormData({
        full_name: "",
        mobile: "",
        email: "",
        whatsapp_number: "",
        communication_address: "",
        wallet_address: "",
        username: "",
        password: "",
        role: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingLifeSaver(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLifeSaver) {
        await api.put(`/life-savers/${editingLifeSaver.id}`, formData);
        toast.success("Members updated successfully");
      } else {
        await api.post("/life-savers", formData);
        toast.success("Members created successfully");
      }
      handleCloseModal();
      fetchLifeSavers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Member?")) {
      try {
        await api.delete(`/life-savers/${id}`);
        toast.success("Member deleted successfully");
        fetchLifeSavers();
      } catch (error) {
        toast.error("Failed to delete Member");
      }
    }
  };

  const handleOpenPasswordModal = (userId) => {
    setSelectedUserId(userId);
    setNewPassword("");
    setPasswordModalOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/auth/reset-password/${selectedUserId}`, { newPassword });
      toast.success("Password reset successfully");
      setPasswordModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Members Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <FiPlus />
          Add Members
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Mobile</th>
                <th>WhatsApp</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lifeSavers.map((lifeSaver) => (
                <tr key={lifeSaver.id}>
                  <td>{lifeSaver.full_name}</td>
                  <td>{lifeSaver.login_username}</td>
                  <td>{lifeSaver.role || "-"}</td>
                  <td>{lifeSaver.mobile}</td>
                  <td>{lifeSaver.whatsapp_number || "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        lifeSaver.is_active ? "badge-success" : "badge-danger"
                      }`}
                    >
                      {lifeSaver.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-btn edit"
                      onClick={() => handleOpenModal(lifeSaver)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="action-btn view"
                      onClick={() => handleOpenPasswordModal(lifeSaver.user_id)}
                      title="Reset Password"
                    >
                      <FiKey />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(lifeSaver.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {lifeSavers.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingLifeSaver ? "Edit Members" : "Add Members"}
      >
        <form onSubmit={handleSubmit}>
          <div>
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Mobile *</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
        <div>
          <div className="form-group">
            <label>WhatsApp Number</label>
            <input
              type="text"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Communication Address</label>
            <textarea
              name="communication_address"
              value={formData.communication_address}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Wallet Address</label>
            <input
              type="text"
              name="wallet_address"
              value={formData.wallet_address}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              placeholder="e.g., Senior Life Saver, Project Leads, Hulk, Pioneer"
            />
          </div>
        </div>
          {!editingLifeSaver && (
            <div className="form-group">
              <label>Password *</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingLifeSaver}
                  minLength="6"
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: "#666",
                  }}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingLifeSaver ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title="Reset Password"
      >
        <form onSubmit={handleResetPassword}>
          <div className="form-group">
            <label>New Password *</label>
            <div style={{ position: "relative" }}>
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength="6"
                placeholder="Enter new password (min 6 characters)"
                style={{ paddingRight: "40px" }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                  color: "#666",
                }}
              >
                {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPasswordModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Reset Password
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default LifeSaverManagement;
